<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="75%">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input lazy"
                @submit.prevent="storeTask">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>{{ serviceType === 0 ? 'New Maintenance Order' : 'Schedule Inspection' }}</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="py-0" cols="6">
                            <v-text-field v-model="formData['label']" :rules="[vRules.required]"
                                          autofocus density="compact"
                                          :label="serviceType === 0 ? 'Order Name' : 'Name'"/>
                        </v-col>
                        <v-col class="py-0" cols="6">
                            <v-select v-model="formData['driver_id']" :items="drivers"
                                      clearable density="compact" :item-title="d => `${d.first_name || ''} ${d.last_name || ''}`.trim()" item-value="id"
                                      label="Assigned Driver"/>
                        </v-col>
                        <v-col v-if="serviceType === 1" class="py-0" cols="6">
                            <v-text-field v-model.number="formData['repeat_days']" density="compact"
                                          label="Repeat every (days)" min="0" type="number"
                                          hint="0 for a one-off. The next one is scheduled this many days after this one is done."
                                          persistent-hint @update:model-value="repeatTouched = true"/>
                        </v-col>
                        <template v-if="!serviceType">
                            <v-col class="py-0" cols="6">
                                <v-select v-model="formData['inspection_task_id']" :items="inspectionTasksList"
                                          clearable density="compact" item-title="label" item-value="id"
                                          label="From Scheduled Inspection"/>
                            </v-col>
                            <v-col class="py-0" cols="6">
                                <v-select v-model="formData['vehicle_service_center_id']" :disabled="serviceCenterDialog"
                                          :items="serviceCenters" density="compact" item-title="name"
                                          item-value="id" label="Service Center">
                                    <template v-slot:append>
                                        <v-icon class="cursor-pointer" icon="mdi-plus" size="small"
                                                @click="serviceCenterDialog = true"/>
                                    </template>
                                </v-select>
                            </v-col>
                        </template>
                    </v-row>

                    <v-row>
                        <v-col class="py-0 my-3" cols="12">
                            <h4>Task List</h4>
                            <v-table>
                                <thead>
                                <tr>
                                    <th class="w-33">{{ serviceType === 0 ? 'Maintenance' : 'Inspection' }} Task</th>
                                    <th style="width: 180px">Due Date</th>
                                    <th>Notes</th>
                                    <th style="width: 15px">
                                        <v-btn color="black" icon="mdi-plus" size="x-small" variant="flat"
                                               @click="addItem"/>
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr v-for="(item,index) in formData['items']" :key="index">
                                    <td class="pa-0">
                                        <v-autocomplete v-model="item['vehicle_service_type_id']"
                                                        :items="services"
                                                        :rules="[vRules.required]"
                                                        density="compact" hide-details item-title="name"
                                                        :label="`Select ${serviceType === 0 ? 'Maintenance' : 'Inspection'} Type`"
                                                        item-value="id"/>
                                        <div v-if="serviceType === 1 && byDriverInspection(item)"
                                             class="text-caption text-grey px-2 pb-1">
                                            <v-icon icon="mdi-clipboard-check-outline" size="14"/>
                                            The driver's next inspection (DVIR) on or after the due date completes it.
                                        </div>
                                    </td>
                                    <td class="pa-0">
                                        <v-text-field v-model="item['service_date']" density="compact" hide-details
                                                      label="Due Date" type="date"/>
                                    </td>
                                    <td class="pa-0">
                                        <v-text-field v-model="item['notes']" density="compact" hide-details
                                                      label="Initial Notes"/>
                                    </td>
                                    <td class="pa-0 text-center">
                                        <v-btn v-if="index" class="mb-3" color="red" icon="mdi-close"
                                               size="small" variant="text" @click="removeItem(index)"/>
                                    </td>
                                </tr>

                                </tbody>
                            </v-table>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" :loading="processingForm" color="primary" size="small"
                           type="submit">Save
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>

    <v-dialog v-model="serviceCenterDialog" persistent scrollable width="350">
        <v-card>
            <v-card-title class="d-flex justify-space-between">
                <h4>New Service Center</h4>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="serviceCenterDialog =false"/>
            </v-card-title>

            <v-container>
                <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                         density="compact" variant="tonal">
                    <p v-for="error in errors">{{ error }}</p>
                </v-alert>

                <v-row>
                    <v-col class="py-1" cols="12">
                        <v-text-field v-model="serviceCenterData['name']" :rules="[vRules.required]"
                                      density="compact" hide-details label="Name"/>
                    </v-col>
                    <v-col class="py-1" cols="12">
                        <v-text-field v-model="serviceCenterData['phone']" density="compact" hide-details
                                      label="Phone"/>
                    </v-col>
                    <v-col class="py-1" cols="12">
                        <v-text-field v-model="serviceCenterData['email']" density="compact" hide-details
                                      label="Email"/>
                    </v-col>
                    <v-col class="py-1" cols="12">
                        <v-text-field v-model="serviceCenterData['address']" density="compact" hide-details
                                      label="Address"/>
                    </v-col>
                </v-row>
            </v-container>

            <v-card-actions class="px-4 d-flex justify-end">
                <v-btn color="black" size="small" variant="outlined" @click="storeServiceCenter">Save</v-btn>
                <v-btn size="small" type="button" variant="outlined"
                       @click="serviceCenterDialog=false">
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, watch} from "vue";
import {refreshMenuCounts} from "@/composables/useMenuCounts";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    serviceType: Number,
    inspectionTasksList: Array,
    serviceCenters: Array,
    services: Array,
    drivers: Array,
    asset: Object,
    inspectionTask: {
        type: Object,
        default: null,
        required: false,
    },
});
const formData = reactive({
    valid: false,
    // A scheduled inspection repeats under the same name, so no date in it.
    label: props.serviceType === 0
        ? `Maintenance order from ${(new Date()).toISOString().split('T')[0]} for ${props.asset.label}`
        : `Scheduled inspection for ${props.asset.label}`,
    repeat_days: props.serviceType === 1 ? 0 : null,
    type: props.serviceType,
    asset_id: props.asset.id,
    vehicle_service_center_id: null,
    driver_id: props.asset.driver_id ?? null,
    inspection_task_id: props.inspectionTask?.id ?? null,
    items: [
        {
            assigned_driver_id: props.asset.driver_id ?? null,
            vehicle_service_type_id: null,
            service_date: null,
            notes: null,
        }
    ]
});
const serviceCenterData = reactive({
    company_id: props.asset.company_id,
    name: null,
    address: null,
    phone: null,
    email: null,
});
const emits = defineEmits(['update:modelValue', 'addServiceCenter', 'addTask']);
const serviceCenterDialog = ref(false);
const dialogSwitch = ref(props.modelValue);
const processingForm = ref(false);
const errors = ref([]);

const addItem = () => {
    formData['items'].push({
        vehicle_service_type_id: null,
        service_date: null,
        notes: null,
    })
}

const serviceFor = (item) => props.services?.find((s) => s.id === item.vehicle_service_type_id);
const byDriverInspection = (item) => !!serviceFor(item)?.completed_by_driver_inspection;

// The repeat follows the types' frequency until the user sets it themselves.
const repeatTouched = ref(false);
watch(() => formData.items.map((i) => i.vehicle_service_type_id), () => {
    if (props.serviceType !== 1 || repeatTouched.value) {
        return;
    }
    const days = formData.items.map(serviceFor)
        .filter((s) => s?.recurring && s?.frequency_days > 0)
        .map((s) => s.frequency_days);
    formData.repeat_days = days.length ? Math.min(...days) : 0;
});
const removeItem = (index) => {
    formData['items'].splice(index, 1)
}
const storeServiceCenter = () => {
    errors.value = [];
    if (!serviceCenterData['name']) {
        errors.value.push("Name field is required");
        return;
    }

    send('post', `fleet/service-centers`, serviceCenterData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('addServiceCenter', response.data.data);
                serviceCenterDialog.value = false;
                serviceCenterData['name'] = null;
                serviceCenterData['phone'] = null;
                serviceCenterData['email'] = null;
                serviceCenterData['address'] = null;
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}
const storeTask = () => {
    errors.value = [];
    processingForm.value = true;
    send('post', `fleet/service-tasks`, formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('addTask', response.data.data);
                emits('update:modelValue', false);
                refreshMenuCounts();
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
        .finally(() => processingForm.value = false);
}
</script>
